import axios from 'axios'
import Swal from './swal' // lazy sweetalert2 (HAP-4635)
import { ref, computed, watch } from 'vue'
import {
	pickInitialCart,
	persistLocalCart,
	hydrateCartFromServer,
	migrateLegacyLocalCart,
	cartLineItemId,
	isPostCheckoutRedirect,
} from './cartStorage'

const query = new Proxy(new URLSearchParams(window.location.search), {
	get: (searchParams, prop) => searchParams.get(prop),
})

const route = { query }

const useCart = (user) => {
	const savingCart = ref(false)
	const couponCode = ref(user?.value?.cart?.coupons?.[0]?.code || '')

	const api = `https://api.churchandfamilylife.com`
	// const api = `http://localhost:3002`

	const cart = ref(pickInitialCart(user))

	const cartTotalItems = computed(() => cart.value?.reduce((a, i) => a + (i.quantity || 0), 0) || 0)

	let skipNextPersist = false

	// HAP-3986: `carts.cartItems.item` only ever validates against `products`
	// (config/collections/carts.js), so a logged-in member's bundle line syncs
	// to the server with its id in the separate `bundle` field instead -- the
	// GraphQL cart fetch comes back with `item` empty for that line. Patch it
	// in client-side from `/bundles` (mirrors refreshGuestPrices below) before
	// normalizeCartLine runs, or the line silently disappears from the cart UI.
	async function hydrateBundleCartItems() {
		const dbItems = user?.value?.cart?.cartItems
		const missing = (dbItems || []).filter((ci) => ci?.bundle && !ci?.item?.id)
		if (!missing.length) return false
		const ids = [...new Set(missing.map((ci) => ci.bundle))]
		try {
			const response = await axios.get(`${api}/bundles`, {
				params: { search: JSON.stringify({ id: { $in: ids } }), limit: ids.length },
			})
			const bundles = response?.data?.response
			if (!bundles?.length) return false
			for (const ci of missing) {
				const bundle = bundles.find((b) => b.id === ci.bundle)
				if (bundle) ci.item = { ...bundle, collection: 'bundles' }
			}
			return true
		} catch (e) {
			console.error('Error hydrating bundle cart lines:', e)
			return false
		}
	}

	async function hydrateFromServerWithBundles() {
		await hydrateBundleCartItems()
		return hydrateCartFromServer(user, cart)
	}

	function hydrateFromServer() {
		skipNextPersist = true
		return hydrateFromServerWithBundles()
	}

	// Guests never touch the server cart, so a stale localStorage price can sit on screen
	// indefinitely while the server re-prices from the DB at checkout (HAP-3981). Refresh
	// price/stock/bulkDiscounts from the live catalog whenever a guest views cart/checkout.
	async function refreshGuestPrices() {
		if (user?.value?.member?.id) return false
		const ids = cart.value?.map(cartLineItemId).filter(Boolean)
		if (!ids?.length) return false
		try {
			const response = await axios.get(`${api}/products`, {
				params: {
					fields: JSON.stringify(['id', 'price', 'stock', 'digital', 'weight', 'bulkDiscounts']),
					search: JSON.stringify({ id: { $in: ids } }),
					limit: ids.length,
				},
			})
			const fresh = response?.data?.response
			if (!fresh?.length) return false
			skipNextPersist = true
			cart.value = cart.value.map((ci) => {
				const product = fresh.find((p) => p.id === cartLineItemId(ci))
				if (!product) return ci
				return { ...ci, item: { ...ci.item, price: product.price, stock: product.stock, digital: product.digital, weight: product.weight, bulkDiscounts: product.bulkDiscounts } }
			})
			persistLocalCart(cart.value, user?.value?.member?.id)
			return true
		} catch (e) {
			console.error('Error refreshing guest cart prices:', e)
			return false
		}
	}

	function shouldPersistCart() {
		if (isPostCheckoutRedirect()) return false
		if (user?.value?.cart?.completed || user?.value?.cart?.finalized) return false
		return true
	}

	async function saveCart() {
		if (!shouldPersistCart()) return
		savingCart.value = true
		try {
			let id = user?.value?.cart?.id
			let path = id ? `carts/${id}` : `carts`
			let cartItems = cart.value.map((ci) => {
				const itemId = cartLineItemId(ci)
				const base = ci.variant ? { quantity: ci.quantity, variant: ci.variant } : { quantity: ci.quantity }
				// HAP-3986: `item` is a Relationship scoped to `products` only -- a bundle
				// id doesn't validate there and gets silently dropped to []. Send it as
				// `bundle` instead, a plain unvalidated field, so it actually persists.
				return ci.item?.collection === 'bundles' ? { ...base, bundle: itemId } : { ...base, item: itemId }
			})
			// Never wipe a finalized cart's server line items when local cart was cleared for Stripe redirect.
			if (!cartItems.length && user?.value?.cart?.finalized) return
			let response = await axios[id ? 'put' : 'post'](`${api}/${path}`, { cartItems })
			let mangoCart = response?.data?.response
			if (mangoCart?.id) {
				skipNextPersist = true
				user.value.cart = mangoCart
				if (mangoCart.cartItems?.length) {
					hydrateFromServerWithBundles()
				}
			}
		} catch (e) {
			console.error('Error saving cart:', e)
		} finally {
			savingCart.value = false
		}
	}

	async function saveCoupon() {
		savingCart.value = true

		try {
			let code = couponCode?.value?.toLowerCase()
			let coupons = []
			if (code && code != user?.value?.cart?.coupons?.[0]?.code) {
				let coupon = (await axios.get(`${api}/discounts?search={"code":"${code}", "type":"coupon"}`)).data?.response?.[0]
				if (!coupon?.id) {
					savingCart.value = false
					return Swal.fire('Invalid Coupon', 'The coupon code you entered is not valid.', 'warning')
				}
				coupons.push(coupon.id)
			}

			let id = user?.value?.cart?.id
			let path = id ? `carts/${id}` : `carts`
			let response = await axios[id ? 'put' : 'post'](`${api}/${path}`, { coupons })
			let mangoCart = response?.data?.response
			if (mangoCart?.id) {
				skipNextPersist = true
				user.value.cart = mangoCart
			}
		} catch (e) {
			console.error('Error saving coupon:', e)
		} finally {
			savingCart.value = false
		}
	}

	const debounceSave = computed(() => {
		let timer
		return (...args) => {
			savingCart.value = true
			clearTimeout(timer)
			timer = setTimeout(() => {
				saveCart.apply(this, args)
			}, 1000)
		}
	})

	watch(
		cart,
		(newCart) => {
			if (skipNextPersist) {
				skipNextPersist = false
				return
			}
			if (!shouldPersistCart()) return
			const memberId = user?.value?.member?.id
			persistLocalCart(newCart, memberId)
			debounceSave.value()
		},
		{ deep: true },
	)

	watch(
		() => user?.value?.cart?.cartItems,
		(dbItems) => {
			const memberId = user?.value?.member?.id
			if (!memberId || !dbItems?.length) return
			if (!cart.value?.length) {
				hydrateFromServerWithBundles()
			}
		},
		{ deep: true },
	)

	watch(
		() => user?.value?.member?.id,
		(memberId) => {
			if (!memberId) return
			migrateLegacyLocalCart(user, cart)
			if (user?.value?.cart?.cartItems?.length && !cart.value?.length) {
				hydrateFromServerWithBundles()
			}
		},
	)

	if (route?.query?.coupon) {
		couponCode.value = route.query.coupon
		saveCoupon()
	}

	if (cartTotalItems.value && shouldPersistCart()) saveCart()

	return {
		cart,
		cartTotalItems,
		savingCart,
		couponCode,
		saveCart,
		saveCoupon,
		hydrateFromServer,
		refreshGuestPrices,
	}
}

export default useCart
export {
	getCartStorageKey,
	parseLocalCart,
	saleAppliesToItem,
	normalizeCartItems,
	cartItemsHavePrices,
	hydrateCartFromServer,
	clearLocalCart,
	clearGuestDraftCartId,
	getGuestDraftCartId,
	setGuestDraftCartId,
	isPostCheckoutRedirect,
} from './cartStorage'
